import { useRetryOnError } from '@/components/sidebar/tabs/use-retry-on-error.hook';
import {
  useGetUserPreferencesQuery,
  useSetUserPreferenceMutation,
} from '@/store/features/user-preferences.feature';
import { useAuth } from '@clerk/react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  APPEARANCE_PREFERENCE,
  APPEARANCE_VERSION,
  appearancesEqual,
  lookOf,
  parseAppearance,
  serializeAppearance,
  type AppearanceLook,
  type AppearanceMode,
  type AppearanceV1,
} from './appearance.util';

const cacheKey = (userId: string) => `cleanlist:appearance:${userId}`;

/**
 * The user whose saved mode this browser's theme last matched. The theme's
 * own copy (`theme`) is one per browser, while the appearance is per user.
 */
const THEME_USER_KEY = 'cleanlist:appearance:theme-user';

/** This browser's copy of the user's appearance, or null. */
export function readCachedAppearance(userId: string): AppearanceV1 | null {
  try {
    const raw = localStorage.getItem(cacheKey(userId));
    return raw === null ? null : parseAppearance(JSON.parse(raw));
  } catch {
    return null;
  }
}

/**
 * Keeps this browser's copy in step with what the server holds; null removes
 * it. The copy is written only from the server's answers and saves it took.
 */
export function writeCachedAppearance(
  userId: string,
  appearance: AppearanceV1 | null
): void {
  try {
    if (appearance)
      localStorage.setItem(
        cacheKey(userId),
        JSON.stringify(serializeAppearance(appearance))
      );
    else localStorage.removeItem(cacheKey(userId));
    localStorage.setItem(THEME_USER_KEY, userId);
  } catch {
    // Storage blocked or full: the first paint shows today's chrome instead.
  }
}

/**
 * The mode the server held when this browser last heard from it for this
 * user, or null when it never has or another user was here since. A theme
 * that differs was changed here and never saved.
 */
function readSyncedMode(
  userId: string,
  cached: AppearanceV1 | null
): AppearanceMode | null {
  try {
    return localStorage.getItem(THEME_USER_KEY) === userId
      ? (cached?.mode ?? null)
      : null;
  } catch {
    return null;
  }
}

/** An appearance for one user, as shown or on its way to the server. */
interface UserAppearance {
  userId: string;
  value: AppearanceV1 | null;
}

export interface AppearanceStateResult {
  /**
   * The appearance on screen: the user's own change, else the saved one,
   * else this browser's copy. Null while there is none.
   */
  appearance: AppearanceV1 | null;
  /** The server's value: undefined until it is back, null when none is saved. */
  saved: AppearanceV1 | null | undefined;
  /**
   * The mode the server held when this browser last heard from it for this
   * user, read as the appearance switches on; null when unknown.
   */
  syncedMode: AppearanceMode | null;
  /** True once the user changed something here; the server's value no longer applies. */
  changed: boolean;
  /** Changes are taken: the saved appearance is back. */
  ready: boolean;
  /** The saved appearance could not be read, and nothing of it is back. */
  failed: boolean;
  /** Reads the saved appearance again. */
  retry: () => void;
  /**
   * Shows and saves a look with the current mode, or null for no appearance
   * at all; false when nothing changed.
   */
  commit: (next: AppearanceLook | null) => boolean;
  /** Saves no appearance, with Undo on a toast. */
  reset: () => void;
}

/**
 * The user's appearance, saved in the user preferences (`appearance_v1`) so
 * it follows them across devices.
 *
 * Until the preferences load it reads this browser's copy and takes no
 * change; a failed read is tried again. A change shows at once. One save is
 * out at a time, as the server rewrites the whole preferences row per request,
 * and only the newest change waits behind it. A failed save with nothing newer
 * waiting goes back to what the server last accepted, keeping the mode, and
 * says so. The mode is the theme's: every save carries the one on screen.
 * Signed out, it reads and writes nothing.
 */
export function useAppearanceState({
  mode,
}: {
  /** The theme's preference, saved with every change. */
  mode: AppearanceMode;
}): AppearanceStateResult {
  const { userId, orgId } = useAuth();
  const active = Boolean(userId);

  // The API answers 403 until the user has an organization.
  const { data, isSuccess, isError, refetch } = useGetUserPreferencesQuery(
    undefined,
    { skip: !active || !orgId, refetchOnReconnect: true }
  );
  useRetryOnError({ isError: active && isError, isSuccess }, refetch);
  const [setPreference] = useSetUserPreferenceMutation();

  const saved = useMemo(
    () =>
      active && data !== undefined
        ? parseAppearance(data?.preferences?.[APPEARANCE_PREFERENCE])
        : undefined,
    [active, data]
  );
  // Read afresh each time the appearance switches on or the user changes.
  const { cached, syncedMode } = useMemo(() => {
    if (!active || !userId) return { cached: null, syncedMode: null };
    const copy = readCachedAppearance(userId);
    return { cached: copy, syncedMode: readSyncedMode(userId, copy) };
  }, [active, userId]);

  const [local, setLocal] = useState<UserAppearance | null>(null);
  const changed = local !== null && local.userId === userId;
  const appearance = !active
    ? null
    : changed
      ? local.value
      : saved !== undefined
        ? saved
        : cached;
  const ready = active && (isSuccess || data !== undefined);
  const failed = active && isError && data === undefined;
  const retry = useCallback(() => void refetch(), [refetch]);

  // The server's answer becomes this browser's copy for the next first paint.
  useEffect(() => {
    if (!active || !userId || saved === undefined || changed) return;
    writeCachedAppearance(userId, saved);
  }, [active, changed, saved, userId]);

  // Read at call time, so two changes in one tick build on each other.
  const current = useRef({ appearance, ready, userId, mode });
  current.current = { appearance, ready, userId, mode };

  const inFlight = useRef(false);
  const queued = useRef<UserAppearance | null>(null);
  // What a failed save goes back to: the last appearance the server accepted,
  // or what the user saw before their first change.
  const confirmed = useRef<UserAppearance | null>(null);

  const send = useCallback(() => {
    if (inFlight.current) return;
    const write = queued.current;
    if (!write) return;
    queued.current = null;
    inFlight.current = true;
    // The whole object: the server merges an object one level in, so every
    // field is sent to replace its older value. Null saves none.
    setPreference({
      key: APPEARANCE_PREFERENCE,
      value: write.value && serializeAppearance(write.value),
    })
      .unwrap()
      .then(
        () => {
          confirmed.current = write;
          if (!queued.current) writeCachedAppearance(write.userId, write.value);
        },
        () => {
          // A newer change is waiting, and it carries this one with it.
          if (queued.current) return;
          const back =
            confirmed.current?.userId === write.userId
              ? confirmed.current.value
              : null;
          // The mode stays as the theme has it and goes with the next save.
          setLocal({
            userId: write.userId,
            value: back && { ...back, mode: current.current.mode },
          });
          toast.error("Couldn't save the appearance");
        }
      )
      .finally(() => {
        inFlight.current = false;
        send();
      });
  }, [setPreference]);

  const commit = useCallback(
    (look: AppearanceLook | null): boolean => {
      const before = current.current;
      if (!before.ready || !before.userId) return false;
      const next: AppearanceV1 | null = look && {
        version: APPEARANCE_VERSION,
        mode: before.mode,
        ...lookOf(look),
      };
      if (appearancesEqual(next, before.appearance)) return false;
      if (confirmed.current?.userId !== before.userId)
        confirmed.current = { userId: before.userId, value: before.appearance };
      const write = { userId: before.userId, value: next };
      setLocal(write);
      current.current = { ...before, appearance: next };
      queued.current = write;
      send();
      return true;
    },
    [send]
  );

  const commitNow = useRef(commit);
  commitNow.current = commit;
  const userNow = useRef(userId);
  userNow.current = userId;

  const reset = useCallback(() => {
    const previous = current.current.appearance;
    const resetFor = current.current.userId;
    if (!previous || !commit(null)) return;
    toast('Appearance reset', {
      action: {
        label: 'Undo',
        onClick: () => {
          if (userNow.current === resetFor) commitNow.current(lookOf(previous));
        },
      },
    });
  }, [commit]);

  return useMemo(
    () => ({
      appearance,
      saved,
      syncedMode,
      changed,
      ready,
      failed,
      retry,
      commit,
      reset,
    }),
    [
      appearance,
      changed,
      commit,
      failed,
      ready,
      reset,
      retry,
      saved,
      syncedMode,
    ]
  );
}
